import type { ReactNode } from "react";
import { TableLoadingOverlay, TablePagination } from "@its-engineering-and-development/ui-foundation/components";
import type { TablePageInfo } from "@its-engineering-and-development/ui-foundation/components";
import { cx } from "@its-engineering-and-development/ui-foundation/utils";
import { LoadingCard } from "@app/components/base/loading-state/loading-card";

/** Builds the uniform loading label shown across every data table (e.g. "Loading users…"). */
export function tableLoadingLabel(tabName: string): string {
    return `Loading ${tabName}…`;
}

/**
 * Standalone loading block matching the {@link DataTableShell} overlay content.
 *
 * Use for first-load states where the table itself cannot render yet (no data), so
 * the loading label stays identical to the in-table overlay: `Loading {tabName}…`.
 * Pass an optional {@link description} to add a secondary line under the label.
 *
 * Thin wrapper over the reusable {@link LoadingCard}.
 */
export function DataTableLoading({ tabName, description, className }: { tabName: string; description?: string; className?: string }) {
    return <LoadingCard label={tableLoadingLabel(tabName)} description={description} className={cx("min-h-40", className)} />;
}

/**
 * Pagination bar that self-hides when there is no data.
 *
 * Enforces the rule that "Showing X–Y of Z" and the Prev/Next buttons must not be
 * rendered when the table has no rows. Reusable directly by pages whose layout does
 * not fit {@link DataTableShell} (e.g. the responsive approval tables).
 */
export function DataTablePagination({
    pageInfo,
    onPageChange,
    isLoadingPage,
}: {
    pageInfo?: TablePageInfo | null;
    onPageChange?: (page: number) => void;
    isLoadingPage?: boolean;
}) {
    if (!pageInfo || !onPageChange || pageInfo.totalCount === 0) return null;
    return <TablePagination pageInfo={pageInfo} onPageChange={onPageChange} isLoadingPage={isLoadingPage} />;
}

/** Standard in-table empty state row. */
export function DataTableEmptyState({ colSpan, message }: { colSpan: number; message: string }) {
    return (
        <tr>
            <td colSpan={colSpan} className="px-4 py-12 text-center text-sm text-gray-500">
                {message}
            </td>
        </tr>
    );
}

/**
 * Generic presentational table shell.
 *
 * Owns the card wrapper, the uniform loading overlay and the pagination bar. Callers
 * provide the `<thead>`/`<tbody>` content and the data hook state; column headers,
 * cells, filtering and sorting stay in the caller.
 *
 * - Loading overlay label is always `Loading {tabName}…`.
 * - Pagination renders only when there is data (see {@link DataTablePagination}).
 */
export function DataTableShell({
    tabName,
    isLoading = false,
    loadingDescription,
    minWidth = "900px",
    pageInfo,
    onPageChange,
    header,
    isEmpty = false,
    emptyMessage,
    hasActiveFilters = false,
    children,
}: {
    /** Name of the current tab/entity, used to build the loading label. */
    tabName: string;
    isLoading?: boolean;
    /** Optional secondary line shown under the loading label in the overlay. */
    loadingDescription?: string;
    minWidth?: string;
    pageInfo?: TablePageInfo | null;
    onPageChange?: (page: number) => void;
    /** The `<thead>` element. Hidden when the table is empty with no active filters. */
    header?: ReactNode;
    /** When true, the table has no rows. */
    isEmpty?: boolean;
    /** Message shown, centered, when {@link isEmpty} is true and the table is not loading. */
    emptyMessage?: ReactNode;
    /**
     * Whether a search/column filter is currently applied. When empty results come from
     * an active filter, the header stays visible so the user can clear it and revert.
     */
    hasActiveFilters?: boolean;
    /** The `<tbody>` element with the data rows. */
    children: ReactNode;
}) {
    // Empty with no filters → collapse to a centered message with no header.
    const emptyNoFilters = isEmpty && !isLoading && !hasActiveFilters && emptyMessage != null;
    // Empty because of an active filter → keep the header so the filter can be reverted.
    const emptyWithFilters = isEmpty && !isLoading && hasActiveFilters && emptyMessage != null;
    const showHeader = !isEmpty || hasActiveFilters;
    return (
        <div className="relative overflow-hidden rounded-xl border border-gray-200 bg-white">
            <TableLoadingOverlay isLoading={isLoading} label={tableLoadingLabel(tabName)} description={loadingDescription} />
            {emptyNoFilters ? (
                <div className="flex min-h-40 items-center justify-center px-4 py-12 text-center text-sm text-gray-500">{emptyMessage}</div>
            ) : (
                <div className="min-h-40 overflow-x-auto">
                    <table className="w-full border-collapse text-sm" style={{ minWidth }}>
                        {showHeader && header}
                        {children}
                        {emptyWithFilters && (
                            <tbody>
                                <tr>
                                    <td colSpan={100} className="px-4 py-16 text-center text-sm text-gray-500">
                                        {emptyMessage}
                                    </td>
                                </tr>
                            </tbody>
                        )}
                    </table>
                </div>
            )}
            <DataTablePagination pageInfo={pageInfo} onPageChange={onPageChange} isLoadingPage={isLoading} />
        </div>
    );
}
